import {Component, ElementRef, EventEmitter, Input, OnInit, Output, Renderer2, ViewChild} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';

@Component({
  selector: 'year-and-quarter-picker',
  templateUrl: './year-and-quarter-picker.component.html',
  styleUrls: ['./year-and-quarter-picker.component.scss']
})
export class YearAndQuarterPickerComponent implements OnInit {

  @ViewChild('toggleButton') toggleButton: ElementRef;
  @ViewChild('datePicker') datePicker: ElementRef;
  @ViewChild('selectedDates') selectedDates: ElementRef;

  @Input() years: number[];
  @Input() quarters: string[];
  @Input() selectedYears: string[];
  @Input() selectedQuarters: string[];


  @Output() submit = new EventEmitter<any>();

  formGroup: FormGroup;

  display = false;
  selectedDateText: string;

  constructor(protected Translate: TranslateService,
              protected formBuilder: FormBuilder) {
  }

  ngOnInit() {
    this.initializeFormGroup();
  }

  initializeFormGroup() {
    this.formGroup = this.formBuilder.group({
      thisYear: new FormControl(this.selectedYears.includes(this.years[0].toString())),
      lastYear: new FormControl(this.selectedYears.includes(this.years[1].toString())),
      twoYearsAgo: new FormControl(this.selectedYears.includes(this.years[2].toString())),
      threeYearsAgo: new FormControl(this.selectedYears.includes(this.years[3].toString())),
      fourYearsAgo: new FormControl(this.selectedYears.includes(this.years[4].toString())),
      fourthQuarter: new FormControl(this.selectedQuarters.includes(this.quarters[0])),
      thirdQuarter: new FormControl(this.selectedQuarters.includes(this.quarters[1])),
      secondQuarter: new FormControl(this.selectedQuarters.includes(this.quarters[2])),
      firstQuarter: new FormControl(this.selectedQuarters.includes(this.quarters[3]))
    });

    this.formatSelectedDateText();
  }

  selectAllDates() {
    this.formGroup.get('thisYear').setValue(true);
    this.formGroup.get('lastYear').setValue(true);
    this.formGroup.get('twoYearsAgo').setValue(true);
    this.formGroup.get('threeYearsAgo').setValue(true);
    this.formGroup.get('fourYearsAgo').setValue(true);
    this.formGroup.get('fourthQuarter').setValue(true);
    this.formGroup.get('thirdQuarter').setValue(true);
    this.formGroup.get('secondQuarter').setValue(true);
    this.formGroup.get('firstQuarter').setValue(true);
  }

  toggleDatePicker() {
    this.display = !this.display;
  }

  submitDatePicker() {
    const selectedYearsAndQuarters = {
      years: [],
      quarters: []
    };
    const today = new Date();
    const formNameToYearMap = {
      thisYear: today.getFullYear(),
      lastYear: today.getFullYear() - 1,
      twoYearsAgo: today.getFullYear() - 2,
      threeYearsAgo: today.getFullYear() - 3,
      fourYearsAgo: today.getFullYear() - 4,
      firstQuarter: 1,
      secondQuarter: 2,
      thirdQuarter: 3,
      fourthQuarter: 4
    };
    for (const field in this.formGroup.controls) {
      const control = this.formGroup.get(field);
      const dataArray = field.includes('Quarter') ? selectedYearsAndQuarters.quarters : selectedYearsAndQuarters.years;
      if (control.value) {
        dataArray.push(formNameToYearMap[field]);
      }
    }
    this.selectedYears = selectedYearsAndQuarters.years;
    this.selectedQuarters = selectedYearsAndQuarters.quarters.map((quarter) => `Q${quarter}`);

    this.submit.emit(selectedYearsAndQuarters);
    this.display = false;

    this.formatSelectedDateText();
  }

  formatRange(items: string[], template: string[]): string {

    // Make shallow copies so that we do not mutate parameters directly
    items = [...items];
    template = [...template];

    let sublists: string[][] = [];

    /**
     * This block performs as follows:
     * While the list of selected items still contains elements,
     * - chop off the front of the template until the
     *   first element of the template matches the first
     *   element of the selected items
     * - Create a new sublist
     * - add all matching consecutive items from the beginning of items
     *   to the sublist.
     * - append the sublist to the result.
     *
     * Then, with all sublists created, format them to either have a
     * hyphenated range or just a value and comma-separate the result.
     */
    while (items.length > 0) {

      // 1.) Remove template elements until the first element of the template matches items
      while (template[0] !== items[0]) {
        template.shift();
      }

      // 2.) Build next list of consecutive matching items
      let currentSublist = [];
      while (items.length > 0 && template[0] === items[0]) {
        currentSublist.push(items.shift());
        template.shift();
      }

      sublists.push(currentSublist);
    }

    let result = sublists.map((sublist) => {
      return sublist.length > 1
        ? `${sublist[0]}-${sublist[sublist.length - 1]}`
        : sublist[0]
    }).join(", ");

    return result;
  }

  formatSelectedDateText() {
    let years = this.formatRange(
      [...this.selectedYears].sort().map((year) => year.toString()),
      [...this.years].sort().map((year) => year.toString())
    );

    let quarters = this.formatRange(
      [...this.selectedQuarters].sort(),
      [...this.quarters].sort());
    this.selectedDateText = `${years} ${quarters}`;
  }

  enableDisableDateCheckboxes(dateType: 'Year'| 'Quarter'): void {
    const fields: string[] = Object.keys(this.formGroup.controls).filter((field) => field.includes(dateType));
    const selectedFields: string[]  = fields.filter(field => this.formGroup.controls[field].value === true);

    if (selectedFields.length === 1) {
      this.formGroup.controls[selectedFields[0]].disable({onlySelf: true});
    } else {
      fields.map(field => this.formGroup.controls[field].enable());
    }
  }
}

